import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Users, X, Search, Plus, Info } from "lucide-react";

/**
 * Meus Secretários — Consultor escolhe até 3 mentores. Só estes veem GPS,
 * missão/visão/valores. Sem seleção → herda comportamento legado (coach
 * do clube pode ver se o toggle de partilha estiver ligado).
 */
const MAX_MENTORS = 3;

export default function MyMentorsPanel() {
  const [mentors, setMentors] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [m, c] = await Promise.all([
        api.get("/gps/mentors"),
        api.get("/coaches"),
      ]);
      setMentors(m.data.mentors || []);
      setCoaches(c.data || []);
    } catch (err) { toast.error(formatApiError(err)); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const addMentor = async (userId) => {
    setSaving(true);
    try {
      await api.post(`/gps/mentors/${userId}`);
      await load();
      setQuery("");
      setPickerOpen(false);
      toast.success("Mentor adicionado e notificado por email.");
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  const removeMentor = async (userId) => {
    if (!window.confirm("Remover este mentor? Deixará de ver o teu GPS e dados sensíveis.")) return;
    setSaving(true);
    try {
      await api.delete(`/gps/mentors/${userId}`);
      await load();
      toast.success("Mentor removido.");
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  const mentorIds = new Set(mentors.map((m) => m.user_id));
  const available = coaches.filter((c) => !mentorIds.has(c.user_id));
  const filtered = query.trim()
    ? available.filter((c) =>
        (c.name || "").toLowerCase().includes(query.toLowerCase()) ||
        (c.club || "").toLowerCase().includes(query.toLowerCase()) ||
        (c.email || "").toLowerCase().includes(query.toLowerCase()),
      )
    : available;

  const canAddMore = mentors.length < MAX_MENTORS;

  return (
    <div className="kw-card p-5" data-testid="my-mentors-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div>
          <div className="kw-overline flex items-center gap-2">
            <Users className="w-3 h-3 text-[#E50000]" /> Meus Secretários · Mentoria
          </div>
          <h2 className="font-display text-xl md:text-2xl font-black uppercase tracking-tight mt-1">
            Quem te <span className="text-[#E50000]">acompanha</span>
          </h2>
          <p className="text-xs text-[#A3A3A3] mt-1 max-w-xl leading-snug">
            Escolhe até <strong className="text-white">{MAX_MENTORS} coaches</strong> para verem o teu GPS, missão, visão e valores.
            Só estes coaches — mais ninguém, exceto o Admin.
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-[#737373]">Mentores</div>
          <div className="font-display text-3xl font-black text-white">
            {mentors.length}<span className="text-[#737373] text-lg">/{MAX_MENTORS}</span>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="kw-overline">A carregar…</div>
      ) : (
        <>
          {mentors.length === 0 && (
            <div className="border border-dashed border-[#333] p-4 text-center" data-testid="mentors-empty">
              <div className="text-sm text-[#A3A3A3]">
                Ainda não escolheste nenhum coach. Sem seleção, aplicam-se as regras de partilha legadas
                (secretário do teu clube pode ver se o toggle &quot;Partilhar com Secretário&quot; estiver ligado no GPS).
              </div>
            </div>
          )}

          {mentors.length > 0 && (
            <div className="space-y-2 mb-3" data-testid="mentors-list">
              {mentors.map((m) => (
                <div
                  key={m.user_id}
                  className="border border-[#22C55E]/40 bg-[#22C55E]/5 p-3 flex items-center gap-3"
                  data-testid={`mentor-item-${m.user_id}`}
                >
                  <div className="w-10 h-10 rounded-full bg-[#E50000]/10 border border-[#E50000] flex items-center justify-center font-bold text-[#E50000]">
                    {(m.name || "?").slice(0, 1).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm">{m.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-[#737373]">
                      {m.club || "—"} · {m.email}
                    </div>
                  </div>
                  <button
                    onClick={() => removeMentor(m.user_id)}
                    disabled={saving}
                    className="p-2 text-[#A3A3A3] hover:text-[#E50000]"
                    title="Remover mentor"
                    data-testid={`mentor-remove-${m.user_id}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {canAddMore ? (
            <>
              {!pickerOpen ? (
                <button
                  onClick={() => setPickerOpen(true)}
                  className="kw-btn-secondary inline-flex items-center gap-2 text-xs"
                  data-testid="mentors-add-btn"
                >
                  <Plus className="w-3.5 h-3.5" /> Adicionar coach
                </button>
              ) : (
                <div className="border border-[#333] p-4" data-testid="mentors-picker">
                  <div className="flex items-center gap-2 mb-3">
                    <Search className="w-4 h-4 text-[#737373]" />
                    <input
                      autoFocus
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Pesquisar secretário por nome, clube ou email…"
                      className="kw-input flex-1"
                      data-testid="mentors-picker-search"
                    />
                    <button
                      onClick={() => { setPickerOpen(false); setQuery(""); }}
                      className="text-[#A3A3A3] hover:text-white p-1"
                      data-testid="mentors-picker-close"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="max-h-64 overflow-y-auto space-y-1">
                    {filtered.length === 0 && (
                      <div className="text-xs text-[#737373] italic py-3 text-center">Sem coaches para mostrar.</div>
                    )}
                    {filtered.map((c) => (
                      <button
                        key={c.user_id}
                        onClick={() => addMentor(c.user_id)}
                        disabled={saving}
                        className="w-full text-left border border-[#222] hover:border-[#E50000] p-3 flex items-center gap-3 transition-colors"
                        data-testid={`mentors-picker-option-${c.user_id}`}
                      >
                        <div className="w-8 h-8 rounded-full bg-[#141414] border border-[#333] flex items-center justify-center text-xs font-bold">
                          {(c.name || "?").slice(0, 1).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm">{c.name}</div>
                          <div className="text-[10px] uppercase tracking-widest text-[#737373]">
                            {c.club || "—"} · {c.email}
                          </div>
                        </div>
                        <Plus className="w-4 h-4 text-[#E50000]" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="border-l-2 border-[#EAB308] bg-[#EAB308]/5 p-3 flex items-start gap-2 text-xs text-[#EAB308]" data-testid="mentors-max-notice">
              <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>Atingiste o limite de {MAX_MENTORS} coaches. Remove um mentor para poderes adicionar outro.</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
